import Image from "next/image";
import React, { useEffect, useRef, useState } from 'react';
import styles from "./ShortsFeedCard.module.scss";
import { ShortsFeedItemData, ShortsContentType } from "@components/organisms/ShortsFeed/ShortsFeed.types";
import ArticleCard from "@components/organisms/ArticleCard/ArticleCard";
import assetLoader from "@utils/internalImageLoder";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { PAGE_TYPES } from "@constants/pageTypes";
import { useIsMobile } from "@hooks/use-mobile";

interface ShortsFeedCardProps {
  item: ShortsFeedItemData;
  handleShare?: (item: ShortsFeedItemData) => void;
  className?: string;
  isMuted: boolean;
  handleMuteToggle: () => void;
  isActive: boolean;
  isPrefetched?: boolean;
  cityName?: string;
  shouldPreload?: boolean;
}
declare global {
  interface Window {
    YT: {
      Player: new (
        elementId: string,
        config: {
          videoId: string;
          playerVars?: Record<string, number | string>;
          events?: {
            onReady?: (event: { target: YTPlayer }) => void;
            onStateChange?: (event: { data: number }) => void;
            onError?: (event: { data: number }) => void;
          };
        }
      ) => YTPlayer;
      PlayerState: {
        ENDED: number;
        PLAYING: number;
        PAUSED: number;
        BUFFERING: number;
        CUED: number;
      };
    };
    onYouTubeIframeAPIReady: () => void;
  }
}

interface YTPlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  mute: () => void;
  unMute: () => void;
  isMuted: () => boolean;
  seekTo: (seconds: number, allowSeekAhead?: boolean) => void;
  destroy: () => void;
}

const ShortsFeedCard: React.FC<ShortsFeedCardProps> = ({ 
  item, 
  className, 
  isMuted, 
  handleMuteToggle, 
  isActive,
  cityName,
  shouldPreload = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();
  const ytPlayerRef = useRef<YTPlayer | null>(null);
  const [playerContainerId, setPlayerContainerId] = useState(`yt-player-${item.id}-${Date.now()}`);
  const [isYTApiReady, setIsYTApiReady] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const isMutedRef = useRef(isMuted);

  const isArticle = item.type === ShortsContentType.ARTICLE;
  const isVideo = item.type === ShortsContentType.VIDEO;
  const isYouTubeVideo = isVideo && item?.sourceUrl?.includes('youtube.com');

  const getYouTubeVideoId = (url: string): string | null => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/);
    return match ? match[1] : null;
  };

  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  useEffect(() => {
    if (!isActive) {
      setIsPlayerReady(false);
    }
  }, [isActive]);

  useEffect(() => {
    setPlayerContainerId(`yt-player-${item.id}-${Date.now()}`);
    setIsPlayerReady(false);
  }, [item.id]);

  useEffect(() => {
    if (!isYouTubeVideo) return;

    if (window.YT && window.YT.Player) {
      setIsYTApiReady(true);
      return;
    }

    const existingScript = document.querySelector('script[src="https://www.youtube.com/iframe_api"]');
    if (existingScript) {
      const checkReady = setInterval(() => {
        if (window.YT && window.YT.Player) {
          setIsYTApiReady(true);
          clearInterval(checkReady);
        }
      }, 100);
      return () => clearInterval(checkReady);
    }

    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      setIsYTApiReady(true);
    };

    return () => {
      if (ytPlayerRef.current) {
        ytPlayerRef.current.destroy();
        ytPlayerRef.current = null;
      }
    };
  }, [isYouTubeVideo]);

  useEffect(() => {
    if (!isYouTubeVideo || !isYTApiReady || !isActive) return;

    const videoId = getYouTubeVideoId(item.sourceUrl);
    if (!videoId) return;

    const initPlayer = () => {
      const container = document.getElementById(playerContainerId);
      if (!container) {
        setTimeout(initPlayer, 100);
        return;
      }

      if (ytPlayerRef.current) {
        try {
          ytPlayerRef.current.destroy();
        } catch (error) {
          console.log('Error destroying existing player:', error);
        }
        ytPlayerRef.current = null;
      }

      ytPlayerRef.current = new window.YT.Player(playerContainerId, {
        videoId,
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          showinfo: 0,
          iv_load_policy: 3,
          fs: 0,
          disablekb: 1,
          cc_load_policy: 0,
          loop: 1,
          playlist: videoId,
        },
        events: {
          onReady: (event) => {
            setIsPlayerReady(true);
            event.target.mute();
            event.target.playVideo();
          },
          onStateChange: (event) => {
            if (event.data === window.YT.PlayerState.PLAYING) {
              if (!isMutedRef.current && ytPlayerRef.current) {
                try {
                  ytPlayerRef.current.unMute();
                } catch (e) {
                  console.log(e)
                }
              }
            }
            if (event.data === window.YT.PlayerState.ENDED) {
              ytPlayerRef.current?.seekTo(0, true);
              ytPlayerRef.current?.playVideo();
            }
          },
        },
      });
    };

    initPlayer();

    return () => {
      if (ytPlayerRef.current) {
        try {
          ytPlayerRef.current.destroy();
        } catch (error) {
          console.log('Error destroying player:', error);
        }
        ytPlayerRef.current = null;
      }
      setIsPlayerReady(false);
    };
  }, [isYouTubeVideo, isYTApiReady, isActive, item.sourceUrl, item.id, playerContainerId]);

  useEffect(() => {
    if (ytPlayerRef.current && isYouTubeVideo && isActive && isPlayerReady) {
      try {
        if (isMuted) {
          ytPlayerRef.current.mute();
        } else {
          ytPlayerRef.current.unMute();
        }
      } catch (error) {
        console.log('Error toggling mute:', error);
      }
    }
  }, [isMuted, isYouTubeVideo, isActive, isPlayerReady]);

  useEffect(() => {
    if (ytPlayerRef.current && isYouTubeVideo && isPlayerReady) {
      try {
        if (isActive) {
          ytPlayerRef.current.seekTo(0, true);
          ytPlayerRef.current.playVideo();
        } else {
          ytPlayerRef.current.pauseVideo();
        }
      } catch (error) {
        console.log('Error controlling playback:', error);
      }
    }
  }, [isActive, isYouTubeVideo, isPlayerReady]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    if (videoRef.current && !isYouTubeVideo) {
      const video = videoRef.current;
      if (isActive) {
        video.currentTime = 0;
        video.muted = true;
        video.play().then(() => {
          if (isMobile && !isMutedRef.current) {
            video.muted = false;
          }
        }).catch(error => {
          console.log('Autoplay prevented:', error);
          video.muted = true;
          video.play().catch(err => console.log('Still failed:', err));
        });
      } else {
        video.pause();
      }
    }
  }, [isYouTubeVideo, isActive, isMobile]);

  useEffect(() => {
    if (isActive) {
      const track = async () => {
        await trackEventWithCommonProps("view", EventType.PAGE_EVENT, {
          pageType: PAGE_TYPES.HOME_PAGE,
          additionalProps: {
            view_type: "view_page_section",
            view_name: "view_homepage_section",
            section: "shorts_feed",
            feed_type: isArticle ? "article" : "video",
            feed_title: item.title,
            city_name: cityName,
          },
        });
      };
      track();
    }
  }, [isActive, item.id, item.title, isArticle, cityName]);

  useEffect(() => {
    const card = cardRef.current;
    if (!card || isMobile) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const newEvent = new WheelEvent('wheel', {
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        deltaZ: e.deltaZ,
        deltaMode: e.deltaMode,
        clientX: e.clientX,
        clientY: e.clientY,
        screenX: e.screenX,
        screenY: e.screenY,
        bubbles: true,
        cancelable: true
      });
      
      const parentContainer = card.closest('.shortsFeedScrollContainer');
      if (parentContainer) {
        parentContainer.dispatchEvent(newEvent);
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    card.addEventListener('wheel', handleWheel, { passive: false });
    card.addEventListener('touchmove', handleTouchMove, { passive: false });

    return () => {
      card.removeEventListener('wheel', handleWheel);
      card.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isMobile]);

  return (
    <div ref={cardRef} className={`${styles.shortsFeedItem} ${className}`}>
      {isArticle ? (
        <ArticleCard
          imageUrl={item.thumbnail}
          title={item.title}
          authorName={item.author}
          authorDate={item.publishedAt}
          description={item.description}
          readMoreLink={item.sourceUrl}
          readMoreText="Read More"
          cityName={cityName}
        />
      ) : isVideo ? (
        <>
          {isYouTubeVideo ? (
            isActive ? (
              <div 
                id={playerContainerId}
                className={styles.youtubePlayerContainer}
              />
            ) : (
              <div className={styles.thumbnailContainer}>
                <Image
                  src={item.thumbnail}
                  alt={item.title}
                  fill
                  style={{ objectFit: 'cover' }}
                  priority={shouldPreload}
                />
              </div>
            )
          ) : (
            <video
              ref={videoRef}
              src={item.sourceUrl}
              className={styles.videoPlayer}
              loop
              muted={isMobile || isMuted}
              autoPlay={true}
              playsInline
              preload="auto"
              poster={item.thumbnail}
            />
          )}
          <div className={styles.gradientOverlay}></div>

          <div className={styles.contentWrapper}>
            <div className={styles.middleSection}>
              <div className={styles.detailsContainer}>
                <h2 className={styles.title}>{item.title}</h2>
                <div className={styles.authorInfo}>
                  <div className={styles.authorText}>
                    {item.author && (
                      <span className={styles.authorName}>
                        {item.author}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {isActive && (
                <div className={styles.actionsContainer}>
                  <div className={styles.actionButton} onClick={handleMuteToggle}>
                    <Image
                      key={isMuted ? 'muted' : 'unmuted'}
                      src={`${assetLoader()}${isMuted ? `/icons/mute-icon.svg` : `/icons/unmuteIcon.svg`}`}
                      alt={isMuted ? 'Mute' : 'Unmute'}
                      width={20}
                      height={20}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default ShortsFeedCard;
